import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useMemo, useCallback } from 'react';
import { useReactFormController, parseIsoDateTime, getIsoDateTime, getTzEndOfDay, isAfter, getTzTodayDate, getTzTimeStates, addSeconds, addMilliseconds, getNow, getTzEndOfYear, getTzEndOfMonth } from '@ninja/utilities';
import { useWebUIConfigOptions } from './ThemeProvider2.js';
import { DatePicker } from './DatePicker2.js';
import { FormFieldErrors } from './FormFieldErrors2.js';
import { FormInputGroup } from './FormInputGroup2.js';

const DatePickerRange = (props) => {
    var _a;
    const endDatePickerRef = useRef(null);
    const { layoutClassName, valueType = 'string', label, isRequired, popover, popoverVariant, maxStartDate, minStartDate, maxEndDate, minEndDate, enableTimeSelection = false, reserveLabelSpacing, fromPlaceholder, toPlaceholder, viewMode, autoSelectTime, disabled, } = props;
    const { onChange: originalOnChangeStartDate, onBlur: onBlurStartDate, value: originalStartDate, errors: startDateErrors, hasErrors: startDateHasErrors, onSetError: onSetErrorStartDate, } = useReactFormController(Object.assign(Object.assign({}, props), { errors: props.startDateErrors, hasErrors: props.startDateHasErrors, field: props.field ? `${props.field}.startDate` : undefined }));
    const isReactForm = !!props.field && !!props.form;
    const { onChange: originalOnChangeEndDate, onBlur: onBlurEndDate, value: originalEndDate, errors: endDateErrors, hasErrors: endDateHasErrors, onSetError: onSetErrorEndDate, formId, } = useReactFormController(Object.assign(Object.assign({}, props), { errors: props.endDateErrors, hasErrors: props.endDateHasErrors, field: props.field ? `${props.field}.endDate` : undefined }));
    const { isRtlLocale } = useWebUIConfigOptions();
    const startDate = useMemo(() => {
        var _a;
        const startDateValue = isReactForm ? originalStartDate : (_a = props.value) === null || _a === void 0 ? void 0 : _a.startDate;
        if (valueType === 'string') {
            return parseIsoDateTime(startDateValue);
        }
        else {
            return startDateValue;
        }
    }, [originalStartDate]);
    const endDate = useMemo(() => {
        var _a;
        const endDateValue = isReactForm ? originalEndDate : (_a = props.value) === null || _a === void 0 ? void 0 : _a.endDate;
        if (valueType === 'string') {
            return parseIsoDateTime(endDateValue);
        }
        else {
            return endDateValue;
        }
    }, [originalEndDate]);
    const onChangeStartDate = useCallback((value) => {
        if (valueType === 'string') {
            originalOnChangeStartDate(getIsoDateTime(value));
        }
        else {
            originalOnChangeStartDate(value);
        }
    }, [originalOnChangeStartDate]);
    const onChangeEndDate = useCallback((value) => {
        if (valueType === 'string') {
            originalOnChangeEndDate(getIsoDateTime(value));
        }
        else {
            originalOnChangeEndDate(value);
        }
    }, [originalOnChangeEndDate]);
    const onChange = useCallback((value) => {
        if (props.onChange) {
            if (valueType === 'string') {
                props === null || props === void 0 ? void 0 : props.onChange({
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    startDate: getIsoDateTime(value === null || value === void 0 ? void 0 : value.startDate),
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    endDate: getIsoDateTime(value === null || value === void 0 ? void 0 : value.endDate),
                });
            }
            else {
                props === null || props === void 0 ? void 0 : props.onChange({
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    endDate: value === null || value === void 0 ? void 0 : value.endDate,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    startDate: value === null || value === void 0 ? void 0 : value.startDate,
                });
            }
        }
    }, [props.onChange]);
    return (jsxs(FormInputGroup, { layoutClassName: layoutClassName, label: label, isRequired: isRequired, popover: popover, popoverVariant: popoverVariant, reserveLabelSpacing: reserveLabelSpacing, formId: formId, hasErrors: startDateHasErrors || endDateHasErrors, children: [jsxs("div", { className: "flex items-center flex-1 w-full gap-2", children: [jsx(DatePicker, { onInvalidDateError: onSetErrorStartDate, value: startDate, hasErrors: startDateHasErrors, valueType: "date", onBlur: onBlurStartDate, fromDate: minStartDate, viewMode: viewMode, toDate: maxStartDate, enableTimeSelection: enableTimeSelection, onChange: (v) => {
                            var _a;
                            const startValue = v;
                            const endValue = endDate ? getTzEndOfDay(endDate) : v;
                            const endDateStillValid = !startValue || (startValue && endValue && isAfter(endValue, startValue));
                            if (isReactForm) {
                                onChangeStartDate === null || onChangeStartDate === void 0 ? void 0 : onChangeStartDate(startValue);
                                onChangeEndDate === null || onChangeEndDate === void 0 ? void 0 : onChangeEndDate(endDateStillValid ? endDate : undefined);
                            }
                            else {
                                onChange({
                                    startDate: startValue,
                                    endDate: endDateStillValid ? endDate : undefined,
                                });
                            }
                            if (startValue) {
                                (_a = endDatePickerRef === null || endDatePickerRef === void 0 ? void 0 : endDatePickerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                            }
                        }, defaultMonth: getTzTodayDate(), placeholder: fromPlaceholder !== null && fromPlaceholder !== void 0 ? fromPlaceholder : `${isRtlLocale ? 'من' : 'From'} DD/MM/YYYY`, autoSelectTime: autoSelectTime, disabled: disabled }), jsx(DatePicker, { onInvalidDateError: onSetErrorEndDate, onBlur: onBlurEndDate, hasErrors: endDateHasErrors, valueType: "date", viewMode: viewMode, value: endDate, fromDate: minEndDate !== null && minEndDate !== void 0 ? minEndDate : startDate, toDate: maxEndDate, enableTimeSelection: enableTimeSelection, onChange: (v) => {
                            const getEndOfMonthOrYear = (v) => {
                                return viewMode === 'years' ? getTzEndOfYear(v) : getTzEndOfMonth(v);
                            };
                            let endValue = v && !enableTimeSelection
                                ? viewMode && (viewMode === 'months' || viewMode === 'years')
                                    ? getEndOfMonthOrYear(v)
                                    : getTzEndOfDay(v)
                                : v;
                            if (v && getTzTimeStates(v).seconds === 0 && enableTimeSelection) {
                                endValue = addSeconds(v, 59);
                                endValue = addMilliseconds(endValue, 999);
                            }
                            if (isReactForm) {
                                onChangeEndDate === null || onChangeEndDate === void 0 ? void 0 : onChangeEndDate(endValue);
                            }
                            else {
                                onChange({
                                    startDate: startDate,
                                    endDate: endValue,
                                });
                            }
                        }, defaultMonth: getTzEndOfDay((_a = minEndDate !== null && minEndDate !== void 0 ? minEndDate : startDate) !== null && _a !== void 0 ? _a : getNow()), placeholder: toPlaceholder !== null && toPlaceholder !== void 0 ? toPlaceholder : `${isRtlLocale ? 'إلى' : 'To'} DD/MM/YYYY`, autoSelectTime: autoSelectTime, disabled: disabled })] }), jsxs("section", { className: `flex justify-between gap-1 w-full ${startDateErrors || endDateErrors ? '' : 'hidden'}`, children: [jsx("div", { className: "flex-1", children: jsx(FormFieldErrors, { errors: startDateErrors }) }), jsx("div", { className: "flex-1", children: jsx(FormFieldErrors, { errors: endDateErrors }) })] })] }));
};

export { DatePickerRange };
